import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  CheckCircleOutline as CheckIcon,
  StarBorder as StarIcon,
  CalendarToday as CalendarIcon,
  MenuBook as CourseIcon,
  Business as BuildingIcon,
  WarningAmber as AlertIcon,
  VerifiedUser as ShieldIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { Analytics } from 'utils/analytics';
import type {
  MembershipStatusResponse,
  RetentionInsightConfidence,
  RetentionInsightItem,
  RetentionInsights,
} from 'utils/types';
import {
  RetentionGuidanceSection,
  RetentionGuidanceHeader,
  RetentionEyebrow,
  RetentionStatusChips,
  RetentionStatusChip,
  RetentionPrimaryAction,
  RetentionPrimaryIcon,
  RetentionFactorRows,
  RetentionFactorRow,
  RetentionFactorIcon,
  RetentionConfidenceTag,
  RetentionInfoButton,
  RetentionSheetScrim,
  RetentionDetailSheet,
  RetentionSheetHandle,
  RetentionSheetHead,
  RetentionSheetIcon,
  RetentionSheetCallout,
  RetentionSheetGrid,
  RetentionSheetButton,
  RetentionFooterNote,
  RetentionTagTone,
} from '../MyStats.styles';

/**
 * Compact "Keep/Maintain Status" guidance for Talent under active
 * grandfathering protection (T2-6060). All copy and confidence labels come
 * from the API's `retentionInsights`; this component only renders and never
 * recalculates Fair Fresh Start business logic.
 */

const CONFIDENCE_LABELS: Record<RetentionInsightConfidence, string> = {
  exact: 'Exact',
  projected: 'Projected',
  depends_on_office_feedback: 'Depends on office feedback',
  on_track: 'On track',
  insufficient_history: 'Insufficient history',
  fallback: 'Only if needed',
};

const CONFIDENCE_TONES: Record<RetentionInsightConfidence, RetentionTagTone> = {
  exact: 'green',
  projected: 'amber',
  depends_on_office_feedback: 'blue',
  on_track: 'green',
  insufficient_history: 'gray',
  fallback: 'gray',
};

const ITEM_ICONS: Record<RetentionInsightItem['icon'], React.ReactElement> = {
  check: <CheckIcon />,
  star: <StarIcon />,
  calendar: <CalendarIcon />,
  course: <CourseIcon />,
  building: <BuildingIcon />,
  alert: <AlertIcon />,
  shield: <ShieldIcon />,
};

export const getRetentionConfidenceLabel = (confidence: RetentionInsightConfidence): string => (
  CONFIDENCE_LABELS[confidence] || 'Projected'
);

const getTone = (confidence: RetentionInsightConfidence): RetentionTagTone => (
  CONFIDENCE_TONES[confidence] || 'blue'
);

const getIcon = (icon: RetentionInsightItem['icon']): React.ReactElement => (
  ITEM_ICONS[icon] || <CheckIcon />
);

interface RetentionGuidanceProps {
  insights: RetentionInsights;
  membershipStatus: MembershipStatusResponse;
  onOpenPath: (path: string) => void;
}

const RetentionGuidance: React.FC<RetentionGuidanceProps> = ({ insights, membershipStatus, onOpenPath }) => {
  const [detailItem, setDetailItem] = useState<RetentionInsightItem | null>(null);

  useEffect(() => {
    Analytics.track('Viewed Retention Guidance', {
      currentStatusCode: membershipStatus.currentStatusCode ?? null,
      protectedStatusCode: membershipStatus.grandfathering?.protectedStatusCode ?? null,
      projectedStatusCode: membershipStatus.grandfathering?.calculatedStatusCode ?? null,
      grandfatheringActive: membershipStatus.grandfathering?.active ?? false,
      primaryActionKey: insights.primaryAction?.key ?? null,
      primaryActionConfidence: insights.primaryAction?.confidence ?? null,
    });
    // Track once per page view of the guidance section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = useCallback((item: RetentionInsightItem) => {
    Analytics.track('Opened Retention Guidance Detail', {
      itemKey: item.key,
      confidence: item.confidence,
      currentStatusCode: membershipStatus.currentStatusCode ?? null,
      projectedStatusCode: membershipStatus.grandfathering?.calculatedStatusCode ?? null,
      grandfatheringActive: membershipStatus.grandfathering?.active ?? false,
    });
    setDetailItem(item);
  }, [membershipStatus]);

  const closeDetail = useCallback(() => setDetailItem(null), []);

  const onDetailCta = useCallback(() => {
    if (detailItem?.ctaPath) {
      onOpenPath(detailItem.ctaPath);
    }
    setDetailItem(null);
  }, [detailItem, onOpenPath]);

  const { primaryAction, supportingItems } = insights;

  return (
    <RetentionGuidanceSection data-testid="retention-guidance">
      <RetentionGuidanceHeader>
        <Box>
          <RetentionEyebrow>{insights.heading}</RetentionEyebrow>
          <Typography variant="h6" fontWeight="bold">{insights.subheading}</Typography>
        </Box>
        <RetentionStatusChips>
          <RetentionStatusChip>Now: <strong>{insights.currentLabel}</strong></RetentionStatusChip>
          {insights.projectedLabel && (
            <RetentionStatusChip>After: <strong>{insights.projectedLabel}</strong></RetentionStatusChip>
          )}
        </RetentionStatusChips>
      </RetentionGuidanceHeader>

      {primaryAction && (
        <RetentionPrimaryAction data-testid="retention-primary-action">
          <RetentionPrimaryIcon>{getIcon(primaryAction.icon)}</RetentionPrimaryIcon>
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.25} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {primaryAction.title}
              </Typography>
              <RetentionConfidenceTag $tone={getTone(primaryAction.confidence)}>
                {getRetentionConfidenceLabel(primaryAction.confidence)}
              </RetentionConfidenceTag>
            </Box>
            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.3}>
              {primaryAction.summary}
            </Typography>
          </Box>
          <RetentionInfoButton
            type="button"
            aria-label={`More about ${primaryAction.title}`}
            onClick={() => openDetail(primaryAction)}
            className="no-fastclick"
          >
            <InfoIcon />
          </RetentionInfoButton>
        </RetentionPrimaryAction>
      )}

      {supportingItems.length > 0 && (
        <RetentionFactorRows>
          {supportingItems.map(item => (
            <RetentionFactorRow key={item.key} data-testid={`retention-factor-${item.key}`}>
              <RetentionFactorIcon $tone={getTone(item.confidence)}>{getIcon(item.icon)}</RetentionFactorIcon>
              <Box>
                <Typography variant="body2" fontWeight={700} lineHeight={1.25}>{item.title}</Typography>
                <Typography variant="caption" color="text.secondary">{item.summary}</Typography>
              </Box>
              <RetentionConfidenceTag $tone={getTone(item.confidence)}>
                {getRetentionConfidenceLabel(item.confidence)}
              </RetentionConfidenceTag>
              <RetentionInfoButton
                type="button"
                aria-label={`More about ${item.title}`}
                onClick={() => openDetail(item)}
                className="no-fastclick"
              >
                <InfoIcon />
              </RetentionInfoButton>
            </RetentionFactorRow>
          ))}
        </RetentionFactorRows>
      )}

      {insights.footerNote && (
        <RetentionFooterNote variant="caption">{insights.footerNote}</RetentionFooterNote>
      )}

      {detailItem && (
        <>
          <RetentionSheetScrim data-testid="retention-detail-scrim" onClick={closeDetail} />
          <RetentionDetailSheet role="dialog" aria-modal="true" aria-label={detailItem.detailTitle} data-testid="retention-detail-sheet">
            <RetentionSheetHandle />
            <RetentionSheetHead>
              <RetentionSheetIcon $tone={getTone(detailItem.confidence)}>
                {getIcon(detailItem.icon)}
              </RetentionSheetIcon>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {detailItem.title} details
                </Typography>
                <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                  {detailItem.detailTitle}
                </Typography>
              </Box>
            </RetentionSheetHead>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              {detailItem.detailBody}
            </Typography>
            <RetentionSheetCallout>
              <strong>Current guidance</strong>
              {detailItem.summary}
            </RetentionSheetCallout>
            <RetentionSheetGrid>
              <div>
                <span>Confidence</span>
                <strong>{getRetentionConfidenceLabel(detailItem.confidence)}</strong>
              </div>
              <div>
                <span>Protected status</span>
                <strong>{insights.currentLabel}</strong>
              </div>
            </RetentionSheetGrid>
            {detailItem.ctaLabel && detailItem.ctaPath && (
              <Box mb={1}>
                <RetentionSheetButton variant="outlined" color="primary" onClick={onDetailCta} className="no-fastclick">
                  {detailItem.ctaLabel}
                </RetentionSheetButton>
              </Box>
            )}
            <RetentionSheetButton variant="contained" color="primary" onClick={closeDetail} className="no-fastclick">
              Got it
            </RetentionSheetButton>
          </RetentionDetailSheet>
        </>
      )}
    </RetentionGuidanceSection>
  );
};

export default RetentionGuidance;
